import React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle,
  FileText,
  MapPin,
  Shield,
  TrendingUp,
  Truck,
  XCircle,
} from 'lucide-react';
import { ExportMenu } from '../../ExportMenu';
import type {
  CarrierProfile as CarrierProfileType,
  Crash,
  Inspection,
  Vehicle,
  Violation,
} from '../../../types/api';
import type { BASICScore } from '../CarrierTabs.types';
import type {
  ViolationsBySeverity,
  ViolationsSummary,
} from './Violations';

export interface OverviewProps {
  profile: CarrierProfileType;
  violations: Violation[];
  inspections: Inspection[];
  crashes: Crash[];
  basics: BASICScore[];
  vehicles: Vehicle[];
  violationsSummary: ViolationsSummary | null;
  violationsBySeverity: ViolationsBySeverity;
  expandedViolationSeverity: string | null;
  setExpandedViolationSeverity: (severity: string | null) => void;
  setSelectedVin: (vin: string | null) => void;
  setSelectedInspection: (inspection: Inspection | null) => void;
}

const Overview: React.FC<OverviewProps> = ({
  profile,
  violations,
  inspections,
  crashes,
  basics,
  vehicles,
  violationsSummary,
  violationsBySeverity,
  expandedViolationSeverity,
  setExpandedViolationSeverity,
  setSelectedVin,
  setSelectedInspection,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Export Buttons - Comprehensive Report */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <ExportMenu
          dataType="all"
          data={null}
          carrierName={profile?.legalName}
          dotNumber={profile?.dotNumber}
          allData={{
            profile,
            violations,
            inspections,
            crashes,
            basics,
            vehicles,
          }}
          buttonText="Export All Data"
          size="sm"
        />
      </div>
    
      {/* Safety Performance Metrics */}
      <div className="card-modern fade-in-up" style={{ animationDelay: '0.1s' }}>
        <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
          <TrendingUp style={{ color: 'var(--color-primary)' }} />
          Safety Performance Metrics
        </h3>
        <p className="text-muted" style={{ marginBottom: 'var(--space-lg)' }}>
          FMCSA metrics show 24-month rolling window. For complete historical data, see Analytics tab.
        </p>
        <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {/* Driver OOS Rate */}
          <div className="stat-box">
            {parseFloat(String(profile.driverOosRate || '0')) <= parseFloat(profile.driverOosRateNationalAverage || '0') ? (
              <CheckCircle size={32} style={{ color: 'var(--color-success)', marginBottom: 'var(--space-sm)' }} />
            ) : (
              <XCircle size={32} style={{ color: 'var(--color-danger)', marginBottom: 'var(--space-sm)' }} />
            )}
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: parseFloat(String(profile.driverOosRate || '0')) <= parseFloat(profile.driverOosRateNationalAverage || '0') ? 'var(--color-success)' : 'var(--color-danger)'
            }}>
              {parseFloat(String(profile.driverOosRate || '0')).toFixed(2)}%
            </div>
            <div className="stat-label">DRIVER OOS RATE</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              National Avg: {profile.driverOosRateNationalAverage}%
            </div>
          </div>
    
          {/* Vehicle OOS Rate */}
          <div className="stat-box">
            {parseFloat(String(profile.vehicleOosRate || '0')) <= parseFloat(profile.vehicleOosRateNationalAverage || '0') ? (
              <CheckCircle size={32} style={{ color: 'var(--color-success)', marginBottom: 'var(--space-sm)' }} />
            ) : (
              <XCircle size={32} style={{ color: 'var(--color-danger)', marginBottom: 'var(--space-sm)' }} />
            )}
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: parseFloat(String(profile.vehicleOosRate || '0')) <= parseFloat(profile.vehicleOosRateNationalAverage || '0') ? 'var(--color-success)' : 'var(--color-danger)'
            }}>
              {parseFloat(String(profile.vehicleOosRate || '0')).toFixed(2)}%
            </div>
            <div className="stat-label">VEHICLE OOS RATE</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              National Avg: {profile.vehicleOosRateNationalAverage}%
            </div>
          </div>
    
          {/* Crash History */}
          <div className="stat-box">
            <AlertTriangle size={32} style={{ color: 'var(--color-warning)', marginBottom: 'var(--space-sm)' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
              {profile.crashTotal ?? 0}
            </div>
            <div className="stat-label">CRASH HISTORY (24 MONTHS)</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {profile.fatalCrash ?? 0} Fatal, {profile.injCrash ?? 0} Injury (FMCSA)
            </div>
          </div>
    
          {/* Inspections */}
          <div className="stat-box">
            <FileText size={32} style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-sm)' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
              {profile.driverInsp ?? 0}
            </div>
            <div className="stat-label">DRIVER INSPECTIONS (24 MO)</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {profile.vehicleInsp ?? 0} Vehicle Inspections (FMCSA)
            </div>
          </div>
        </div>
      </div>
    
      {/* Violation Summary */}
      {violations.length > 0 && (
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
            <AlertCircle style={{ color: 'var(--color-warning)' }} />
            Violation Summary by Severity
          </h3>
          <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {/* Critical */}
            <div
              className="stat-box"
              style={{
                borderLeft: '4px solid var(--color-danger)',
                cursor: violationsBySeverity.CRITICAL.length > 0 ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => violationsBySeverity.CRITICAL.length > 0 && setExpandedViolationSeverity(expandedViolationSeverity === 'CRITICAL' ? null : 'CRITICAL')}
              onMouseEnter={(e) => violationsBySeverity.CRITICAL.length > 0 && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                {violationsSummary ? violationsSummary.bySeverity.critical : violationsBySeverity.CRITICAL.length}
              </div>
              <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>Critical</div>
              {violationsBySeverity.CRITICAL.length > 0 && (
                <div className="badge-modern badge-danger" style={{ fontSize: '0.75rem' }}>
                  Click to view
                </div>
              )}
            </div>
    
            {/* High */}
            <div
              className="stat-box"
              style={{
                borderLeft: '4px solid var(--color-warning)',
                cursor: violationsBySeverity.HIGH.length > 0 ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => violationsBySeverity.HIGH.length > 0 && setExpandedViolationSeverity(expandedViolationSeverity === 'HIGH' ? null : 'HIGH')}
              onMouseEnter={(e) => violationsBySeverity.HIGH.length > 0 && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                {violationsSummary ? violationsSummary.bySeverity.high : violationsBySeverity.HIGH.length}
              </div>
              <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>High</div>
              {violationsBySeverity.HIGH.length > 0 && (
                <div className="badge-modern badge-warning" style={{ fontSize: '0.75rem' }}>
                  Click to view
                </div>
              )}
            </div>
    
            {/* Medium */}
            <div
              className="stat-box"
              style={{
                borderLeft: '4px solid #eab308',
                cursor: violationsBySeverity.MEDIUM.length > 0 ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => violationsBySeverity.MEDIUM.length > 0 && setExpandedViolationSeverity(expandedViolationSeverity === 'MEDIUM' ? null : 'MEDIUM')}
              onMouseEnter={(e) => violationsBySeverity.MEDIUM.length > 0 && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                {violationsSummary ? violationsSummary.bySeverity.medium : violationsBySeverity.MEDIUM.length}
              </div>
              <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>Medium</div>
              {violationsBySeverity.MEDIUM.length > 0 && (
                <div className="badge-modern" style={{ fontSize: '0.75rem', backgroundColor: '#fef3c7', color: '#92400e' }}>
                  Click to view
                </div>
              )}
            </div>
    
            {/* Low */}
            <div
              className="stat-box"
              style={{
                borderLeft: '4px solid var(--color-success)',
                cursor: violationsBySeverity.LOW.length > 0 ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => violationsBySeverity.LOW.length > 0 && setExpandedViolationSeverity(expandedViolationSeverity === 'LOW' ? null : 'LOW')}
              onMouseEnter={(e) => violationsBySeverity.LOW.length > 0 && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                {violationsSummary ? violationsSummary.bySeverity.low : violationsBySeverity.LOW.length}
              </div>
              <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>Low</div>
              {violationsBySeverity.LOW.length > 0 && (
                <div className="badge-modern badge-success" style={{ fontSize: '0.75rem' }}>
                  Click to view
                </div>
              )}
            </div>
          </div>
    
          {/* Expanded Violation Details */}
          {expandedViolationSeverity && violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].length > 0 && (
            <div className="card-modern" style={{
              marginTop: 'var(--space-lg)',
              background: '#f9fafb',
              borderLeft: `4px solid ${
                expandedViolationSeverity === 'CRITICAL' ? 'var(--color-danger)' :
                expandedViolationSeverity === 'HIGH' ? '#f97316' :
                expandedViolationSeverity === 'MEDIUM' ? 'var(--color-warning)' :
                'var(--color-success)'
              }`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
                <h4 style={{ fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  {expandedViolationSeverity} Violations ({violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].length})
                </h4>
                <button
                  onClick={() => setExpandedViolationSeverity(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-gray-500)'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-1">
                  {violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].map((violation, idx) => {
                    const isOOS = violation.outOfServiceIndicator === 'Y' || violation.outOfService === true;
                    const unitType = violation.inspViolUnit === 'D' ? 'Driver' :
                                     violation.unitType === '11' || violation.unitType === 'TRUCK' ? 'Truck' :
                                     violation.unitType === '9' || violation.unitType === 'TRAILER' ? 'Trailer' : 'Vehicle';
    
                    return (
                      <div key={idx} className="w-full max-w-[600px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                        <div className={`border-l-[4px] ${isOOS ? 'border-red-500' : 'border-green-500'}`}>
                          <div className="p-[10px]">
                            {/* Header: Badges */}
                            <div className="flex flex-wrap items-center gap-1 mb-1.5">
                              <span className="px-1 py-[2px] bg-slate-100 text-slate-700 text-xs font-semibold rounded border border-slate-200 font-mono">
                                {violation.violationCode || violation.violation_code || (violation.partNo && violation.partNoSection ? `${violation.partNo}.${violation.partNoSection}` : 'N/A')}
                              </span>
    
                              {isOOS && (
                                <span className="px-1 py-[2px] bg-red-50 text-red-700 text-xs font-bold rounded border border-red-100 flex items-center gap-[2px]">
                                  <AlertCircle size={12} /> OOS
                                </span>
                              )}
    
                              <span className="px-1 py-[2px] bg-gray-50 text-gray-500 text-xs font-medium rounded border border-gray-100">
                                {violation.basicCategory || 'Unknown'}
                              </span>
    
                              <span className="px-1 py-[2px] bg-indigo-50 text-indigo-600 text-xs font-medium rounded border border-indigo-100">
                                {unitType}
                              </span>
                            </div>
    
                            {/* Main Description */}
                            <h3 className="text-gray-900 font-semibold text-lg mb-2 leading-tight">
                              {violation.description || violation.violation_description || 'Violation description not available'}
                            </h3>
    
                            {/* Vehicle Card - Compact Gray Box */}
                            <div className="bg-slate-50 rounded-md border border-slate-100 p-[6px] mb-2">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-1 mb-[2px]">
                                    <Truck size={14} className="text-slate-400" />
                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicle Details</span>
                                  </div>
                                  {violation.vin && violation.vin !== 'N/A' ? (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedVin(violation.vin);
                                      }}
                                      className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                                    >
                                      {violation.vin}
                                    </button>
                                  ) : (
                                    <div className="font-mono text-sm font-medium text-slate-400">VIN not available</div>
                                  )}
                                  {violation.unitMake && (
                                    <div className="text-xs text-slate-500 mt-[2px]">
                                      Unit: {violation.inspViolUnit === 'D' ? 'Driver' : `Vehicle ${violation.inspViolUnit}`}
                                      {violation.unitMake && ` - ${violation.unitMake}`}
                                    </div>
                                  )}
                                </div>
                                {violation.allUnits && violation.allUnits.length > 1 && (
                                  <div className="text-xs font-medium bg-white text-slate-500 px-1 py-[2px] rounded shadow-sm border border-slate-200">
                                    {violation.allUnits.length} Units Insp.
                                  </div>
                                )}
                              </div>
                            </div>
    
                            {/* Footer: Inspection Meta Data */}
                            <div className="flex flex-wrap items-center justify-between pt-2 border-t border-gray-100 text-sm">
                              <div className="flex items-center gap-2">
                                {violation.inspectionId && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const inspection = inspections.find((i) => i.inspectionId === violation.inspectionId);
                                      if (inspection) {
                                        setSelectedInspection(inspection);
                                      }
                                    }}
                                    className="flex items-center gap-[3px] group cursor-pointer hover:opacity-80"
                                  >
                                    <FileText size={14} className="text-blue-500" />
                                    <span className="font-medium text-blue-600 group-hover:text-blue-700 underline decoration-blue-200 underline-offset-2">
                                      #{violation.inspectionId}
                                    </span>
                                  </button>
                                )}
                                <div className="flex items-center gap-[3px] text-gray-500">
                                  <Calendar size={14} />
                                  <span>{violation.inspectionDate ? new Date(violation.inspectionDate).toLocaleDateString() : 'N/A'}</span>
                                </div>
                              </div>
    
                              <div className="flex items-center gap-[3px] text-gray-500 mt-1 sm:mt-0">
                                <MapPin size={14} />
                                <span className="uppercase text-[11px] font-bold tracking-wide bg-gray-100 px-[3px] py-[1px] rounded text-gray-600">
                                  {violation.inspectionState || 'N/A'}
                                </span>
                                <span className="text-xs font-medium truncate max-w-[150px]" title={violation.inspectionLocation}>
                                  {violation.inspectionLocation || 'Location not available'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    
      {/* Insurance Information */}
      <div className="card-modern fade-in-up" style={{ animationDelay: '0.3s' }}>
        <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
          <Shield style={{ color: 'var(--color-primary)' }} />
          Insurance Information
        </h3>
        <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {/* BIPD Insurance */}
          <div className="stat-box" style={{ borderLeft: '4px solid var(--color-primary)' }}>
            <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>BIPD INSURANCE (Bodily Injury/Property Damage)</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                ${profile.bipdInsuranceOnFile ? (parseInt(String(profile.bipdInsuranceOnFile)) * 1000).toLocaleString() : '0'}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', alignItems: 'center' }}>
              {profile.bipdInsuranceRequired === 'Y' && (
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                  Required: ${profile.bipdRequiredAmount ? (parseInt(String(profile.bipdRequiredAmount)) * 1000).toLocaleString() : '0'}
                </span>
              )}
              {profile.bipdInsuranceRequired === 'Y' &&
               parseInt(String(profile.bipdInsuranceOnFile || '0')) >= parseInt(String(profile.bipdRequiredAmount || '0')) ? (
                <span className="badge-modern badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle size={12} /> Compliant
                </span>
              ) : profile.bipdInsuranceRequired === 'Y' ? (
                <span className="badge-modern badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <XCircle size={12} /> Insufficient
                </span>
              ) : profile.bipdInsuranceRequired === 'N' ? (
                <span className="badge-modern badge-neutral">Not Required</span>
              ) : null}
            </div>
          </div>
    
          {/* Cargo Insurance */}
          <div className="stat-box" style={{ borderLeft: '4px solid var(--color-warning)' }}>
            <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>CARGO INSURANCE</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                ${profile.cargoInsuranceOnFile ? (parseInt(String(profile.cargoInsuranceOnFile)) * 1000).toLocaleString() : '0'}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', alignItems: 'center' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Required: {profile.cargoInsuranceRequired === 'Y' ? 'Yes' : profile.cargoInsuranceRequired === 'N' ? 'No' : 'Unknown'}
              </span>
              {profile.cargoInsuranceRequired === 'Y' && parseInt(String(profile.cargoInsuranceOnFile || '0')) > 0 ? (
                <span className="badge-modern badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle size={12} /> On File
                </span>
              ) : profile.cargoInsuranceRequired === 'Y' ? (
                <span className="badge-modern badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <XCircle size={12} /> Missing
                </span>
              ) : profile.cargoInsuranceRequired === 'N' ? (
                <span className="badge-modern badge-neutral">Not Required</span>
              ) : null}
            </div>
          </div>
    
          {/* Bond Insurance */}
          <div className="stat-box" style={{ borderLeft: '4px solid var(--color-gray-400)' }}>
            <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>BOND/SURETY</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                ${profile.bondInsuranceOnFile ? parseInt(String(profile.bondInsuranceOnFile)).toLocaleString() : '0'}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', alignItems: 'center' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Required: {profile.bondInsuranceRequired === 'Y' ? 'Yes' : profile.bondInsuranceRequired === 'N' ? 'No' : 'Unknown'}
              </span>
              {profile.bondInsuranceRequired === 'Y' && parseInt(String(profile.bondInsuranceOnFile || '0')) > 0 ? (
                <span className="badge-modern badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle size={12} /> On File
                </span>
              ) : profile.bondInsuranceRequired === 'Y' ? (
                <span className="badge-modern badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <XCircle size={12} /> Missing
                </span>
              ) : profile.bondInsuranceRequired === 'N' ? (
                <span className="badge-modern badge-neutral">Not Required</span>
              ) : null}
            </div>
          </div>
        </div>
        <p className="text-muted" style={{ marginTop: 'var(--space-lg)', fontSize: '0.8125rem' }}>
          Insurance data sourced from FMCSA. Amounts displayed in USD. BIPD = Bodily Injury and Property Damage liability coverage.
        </p>
      </div>
    </div>
  );
};

export default Overview;
